import { Container } from "react-bootstrap";
import MainMenu from "./dashboard/main-menu";
import SideBar from "./dashboard/side-bar";
import FlexBox from "../common/flex-box";
import { useContext, useEffect, useState, useRef, useCallback } from "react";
import { AppContext } from "lib/contexts/app-context";
import { useNavigate } from "react-router-dom";
import useAuthentication from "lib/hooks/useAuthentication";
import { Toaster } from "sonner";
import SimpleBreadCrumb from "../common/simple-breadcrumb";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import UnavailableOverlay from "../common/unavailable-overlay";
import usePractitioner from "lib/hooks/usePractitioner";
import { USER_ROLES } from "lib/constants";

interface DashboardLayoutProps {
  isUnavailable?: boolean;
  children: React.ReactNode;
  breadcrumbItems?: BreadcrumbItem[];
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  isUnavailable = false,
  breadcrumbItems,
  children,
}) => {
  const { appState, updateAppState } = useContext(AppContext);
  const { signOutUser } = useAuthentication();
  const navigateTo = useNavigate();
  const [width, setWidth] = useState(window.innerWidth);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const logoutUser = () => {
    signOutUser();
    navigateTo(PAGE_ROUTES.AUTHENTICATION.Signout);
  };

  const closeSidebar = useCallback(() => {
    if (width < 768 && !appState.isCollapsed) {
      updateAppState({ ...appState, isCollapsed: false });
    }
  }, [width, isSidebarCollapsed]);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        width < 768 &&
        !isSidebarCollapsed
      ) {
        closeSidebar();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [width, isSidebarCollapsed, closeSidebar]);

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
      if (window.innerWidth < 768) {
        updateAppState({ ...appState, isCollapsed: true });
      } else {
        updateAppState({ ...appState, isCollapsed: false });
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Container fluid className="page-wrapper">
      <MainMenu
        onSignOutUser={logoutUser}
        setIsCollapsed={(value) => {
          updateAppState({ ...appState, isCollapsed: value });
        }}
        isCollapsed={appState.isCollapsed ?? false}
      />
      <div
        className="main-content">

        <div ref={sidebarRef} className="transition-all duration-300">
          <SideBar
            setIsCollapsed={(value) => {
              updateAppState({ ...appState, isCollapsed: width < 768 ? value : false })
            }}
            isCollapsed={appState.isCollapsed ?? false}
          />
        </div>

        <Container
          fluid
          className={`page-content px-3 ${appState.isCollapsed ? "sidebar-close" : "sidebar-open"
            }`}
        >
          <SimpleBreadCrumb items={breadcrumbItems} />
          {isUnavailable === true ? <UnavailableOverlay /> : <>{children}</>}
        </Container>
      </div>
      <Toaster />
    </Container>
  );
};

export default DashboardLayout;